import type { StateCreator } from 'zustand/vanilla';
import type { ModalConfigMap, ModalType } from '@/app/types';

/**
 * 是否在路由切换时关闭弹窗配置
 * 未配置的弹窗默认关闭
 * 配置的函数返回 true 表示关闭，false 表示保持打开
 */
const MODAL_ENABLE_CLOSE_ON_ROUTE_CHANGE_CONFIG: Partial<
  Record<ModalType, (currentPath: string, previousPath: string) => boolean>
> = {
  frameShotDialog: (currentPath: string, previousPath: string) => {
    // 抽帧弹窗在抽完回填结束后，不需要关闭。在create页面从参考生图抽帧应用到图生时，因为路由变化，会导致该弹窗关闭。新增跳转前后的路径判断，如果在create页面的话不关闭
    return !currentPath.includes('/create/') || !previousPath.includes('/create/');
  },
};

export interface GlobalModalState {
  modals: {
    [K in ModalType]: ModalConfigMap[K] | null;
  };
}

export interface GlobalModalStore extends GlobalModalState {
  /** 打开弹窗 */
  openModal: <T extends ModalType>(type: T, props?: Omit<ModalConfigMap[T], 'open'>) => void;

  /** 关闭弹窗 */
  closeModal: (type: ModalType) => void;

  /** 关闭所有弹窗 */
  closeAllModals: () => void;

  /** 选择性关闭弹窗 - 根据路由持久化配置决定是否关闭 */
  closeModalsOnRouteChange: (currentPath: string, previousPath?: string) => void;

  /** 更新弹窗配置 */
  updateModalConfig: <T extends ModalType>(type: T, props: Partial<Omit<ModalConfigMap[T], 'open'>>) => void;

  /** 获取弹窗状态 */
  getModalState: <T extends ModalType>(type: T) => ModalConfigMap[T] | null;

  /** 检查弹窗是否打开 */
  isModalOpen: (type: ModalType) => boolean;
}

const initialState: GlobalModalState = {
  modals: {
    actionDialog: null,
    shareDialog: null,
    deleteDialog: null,
    newsletterSubscriptionDialog: null,
    creditDetailsDialog: null,
    taskCenterDrawer: null,
    questionnaire: null,
    planDetailDialog: null,
    clawPassContentDialog: null,
    clawRetentionDialog: null,
    clawAgreementChangeDialog: null,
    purchaseCreditsDialog: null,
    downloadAppDialog: null,
    confirmDialog: null,
    upscaleDialog: null,
    postsModal: null,
    frameShotDialog: null,
    couponDialog: null,
    submitErrorDialog: null,
    googleRewardedAd: null,
    noWatermarkAdsDialog: null,
    insufficientCreditsAdsDialog: null,
    agentInviteDialog: null,
    simpleCreationDetailModal: null,
    loginDialog: null,
    cancelReasonQuestionnaire: null,
    clawPassUsageDialog: null,
    renameOnConflictDialog: null,
  },
};

export const createGlobalModalSlice: StateCreator<GlobalModalStore, [], [], GlobalModalStore> = (set, get) => ({
  ...initialState,

  openModal: <T extends ModalType>(type: T, props?: Omit<ModalConfigMap[T], 'open'>) => {
    const { modals } = get();

    const propsWithOpen = {
      ...(props || {}),
      open: true,
    };

    set({
      modals: {
        ...modals,
        [type]: propsWithOpen,
      },
    });
  },

  closeModal: (type) => {
    const { modals } = get();

    set({
      modals: {
        ...modals,
        [type]: null,
      },
    });
  },

  closeAllModals: () => {
    set({
      modals: { ...initialState.modals },
    });
  },

  closeModalsOnRouteChange: (currentPath: string, previousPath?: string) => {
    const { modals } = get();
    const openModals = (Object.entries(modals) as Array<[ModalType, ModalConfigMap[ModalType]]>).filter(
      ([, modal]) => modal !== null,
    );
    if (openModals.length === 0) return;

    // 收集需要关闭的弹窗类型
    const modalsToClose: ModalType[] = [];
    openModals.forEach(([modalType]) => {
      // 获取配置的 enableCloseOnRouteChange 函数
      const enableCloseOnRouteChange = MODAL_ENABLE_CLOSE_ON_ROUTE_CHANGE_CONFIG[modalType];
      // 有配置的弹窗，根据 enableCloseOnRouteChange 函数判断
      if (enableCloseOnRouteChange) {
        if (previousPath && enableCloseOnRouteChange(currentPath, previousPath)) {
          modalsToClose.push(modalType);
        }
      } else {
        // 没有配置的弹窗默认关闭
        modalsToClose.push(modalType);
      }
    });

    // 只有在需要关闭弹窗时才更新状态
    if (modalsToClose.length > 0) {
      const newModals = { ...modals };
      modalsToClose.forEach((modalType) => {
        newModals[modalType] = null;
      });

      set({ modals: newModals });
    }
  },

  updateModalConfig: (type, props) => {
    const { modals } = get();

    const currentProps = modals[type];
    if (currentProps) {
      set({
        modals: {
          ...modals,
          [type]: {
            ...currentProps,
            ...props,
          },
        },
      });
    }
  },

  getModalState: (type) => {
    const { modals } = get();
    return modals[type];
  },

  isModalOpen: (type) => {
    const { modals } = get();
    return modals[type] !== null;
  },
});
